import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FaPlus } from 'react-icons/fa';
import { createApiClient } from '../../api/client';
import { useCaseContext } from '../context/CaseContext';
import { useUrgencyTabs, UrgencyTabBar, mapUrgencyToTab, CATEGORIES } from '../components/UrgencyTabs';
import ToggleSwitch from '../components/ToggleSwitch';
import '../mobile.css';

const truncate = (text = '', wordLimit = 12) => {
  const words = text.trim().split(/\s+/);
  return words.length > wordLimit ? `${words.slice(0, wordLimit).join(' ')}...` : text;
};

const getTaskKey = (task, index) => (task.id ? String(task.id) : `${task.description || 'task'}__${index}`);

const MobileTasks = ({ API_URL }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { fullFIR, setFullFIR } = useCaseContext();
  const fir = location.state?.fir || fullFIR;
  const tasks = fir?.urgentTasks || [];
  const isStatement = Boolean(location.state?.statement);

  const [expandedKey, setExpandedKey] = useState(null);
  const [statusByKey, setStatusByKey] = useState(() => {
    const initial = {};
    tasks.forEach((t, i) => { initial[getTaskKey(t, i)] = !!t.status; });
    return initial;
  });

  const { activeTab, setActiveTab, hasUrgency, tabCounts, visibleItems } = useUrgencyTabs(tasks, (t) => t.category);

  const pendingCounts = {};
  CATEGORIES.forEach((cat) => {
    pendingCounts[cat] = tasks
      .filter((t) => mapUrgencyToTab(t.category) === cat)
      .filter((t, i) => statusByKey[getTaskKey(t, i)] !== true).length;
  });

  const toggleStatus = async (taskKey, task) => {
    const prev = !!statusByKey[taskKey];
    setStatusByKey((s) => ({ ...s, [taskKey]: !prev }));

    try {
      const api = createApiClient(API_URL);
      const response = await api.post(`/fir/tasks/status/${fir.id}`, {
        task: task.description,
        status: !prev ? 'completed' : 'pending',
      });
      if (response.status === 200) setFullFIR(response.data);
    } catch {
      setStatusByKey((s) => ({ ...s, [taskKey]: prev }));
    }
  };

  if (!fir) return <p style={{ textAlign: 'center', fontWeight: 700 }}>No case data available.</p>;

  return (
    <div style={{ position: 'relative', minHeight: '60vh' }}>
      <UrgencyTabBar hasUrgency={hasUrgency} activeTab={activeTab} setActiveTab={setActiveTab} tabCounts={tabCounts} badgeCounts={pendingCounts} />

      {visibleItems.map((task, index) => {
        const key = getTaskKey(task, index);
        const isOpen = expandedKey === key;
        const isCompleted = !!statusByKey[key];

        return (
          <div key={key} className="m-card" onClick={() => setExpandedKey(isOpen ? null : key)} style={{ cursor: 'pointer' }}>
            <div style={{ fontSize: '14px', fontWeight: 600, color: 'grey', marginBottom: '10px' }}>Task {index + 1}</div>
            <div style={{ fontSize: '14px', fontWeight: 600, color: '#111', textAlign: 'justify' }}>
              {isOpen ? task.description : truncate(task.description)}
            </div>

            {isOpen && (
              <>
                {task.urgency && <div style={{ marginTop: '6px', textAlign: 'justify' }}><b>Urgency: </b>{task.urgency}</div>}
                <div style={{ marginTop: '6px', textAlign: 'justify' }}><b>Source: </b>{task.source || 'FIR'}</div>
              </>
            )}

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '10px' }}>
              {!isStatement ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <ToggleSwitch checked={isCompleted} onChange={() => toggleStatus(key, task)} />
                  <span style={{ fontSize: '13px', color: isCompleted ? '#4CAF50' : '#E53935' }}>
                    {isCompleted ? 'Completed' : 'Pending'}
                  </span>
                </div>
              ) : <span />}
              <span style={{ fontSize: '14px', fontWeight: 600, color: '#5B7CFF' }}>{isOpen ? 'Hide' : 'Show'}</span>
            </div>
          </div>
        );
      })}

      {!isStatement && (
        <button
          onClick={() => navigate('/m/fir/tasks/new', { state: { fir } })}
          className="m-chat-fab"
          style={{ right: '30px', bottom: 'calc(130px + env(safe-area-inset-bottom, 0px))', background: '#094268' }}
          aria-label="Add task"
        >
          <FaPlus size={22} color="#fff" />
        </button>
      )}
    </div>
  );
};

export default MobileTasks;
